// creator-portal/src/pages/Register.tsx
// ✅ UPDATED: Added Vastavik ID (ownerId) and Country fields to match iOS

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import '../styles/Auth.css';
import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

// Country list (common countries first)
countries.registerLocale(en);

const COUNTRY_OPTIONS = Object.entries(
  countries.getNames('en', { select: 'official' })
)
  .map(([code, name]) => ({
    code,
    name,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

//Converts whatever text is in the country input (typed, or browser
// autofill like "India", "IN", "IND", "United States") into an ISO alpha-2 code.
// Returns '' if it can't be resolved.
const resolveCountryCode = (raw: string): string => {
  const v = raw.trim();
  if (!v) return '';

  // "IN" style
  if (/^[A-Za-z]{2}$/.test(v) && countries.isValid(v.toUpperCase())) {
    return v.toUpperCase();
  }

  // "IND" style
  if (/^[A-Za-z]{3}$/.test(v)) {
    const a2 = countries.alpha3ToAlpha2(v.toUpperCase());
    if (a2) return a2;
  }

  // Exact name match (case-insensitive)
  const byName = COUNTRY_OPTIONS.find(
    (c) => c.name.toLowerCase() === v.toLowerCase()
  );
  if (byName) return byName.code;

  // Common names / aliases (e.g. "United States", "USA")
  return countries.getAlpha2Code(v, 'en') || '';
};

// Password validation helper
interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
  passwordsMatch: boolean;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    ownerId: '',      // ✅ Vastavik ID
    country: '',      // ✅ Country code
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Owner ID availability state
  const [ownerIdStatus, setOwnerIdStatus] = useState<{
    isChecking: boolean;
    isAvailable: boolean | null;
    suggestions: string[];
  }>({
    isChecking: false,
    isAvailable: null,
    suggestions: [],
  });

  // Real-time password validation
  const passwordValidation = useMemo((): PasswordValidation => {
    const { password, confirmPassword } = formData;
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      passwordsMatch: password === confirmPassword && confirmPassword.length > 0,
    };
  }, [formData.password, formData.confirmPassword]);

  // Check if password meets minimum requirements
  const isPasswordValid = useMemo(() => {
    return (
      passwordValidation.minLength &&
      passwordValidation.hasUppercase &&
      passwordValidation.hasLowercase &&
      passwordValidation.hasNumber
    );
  }, [passwordValidation]);

  // Owner ID validation
  const ownerIdValidation = useMemo(() => {
    const { ownerId } = formData;
    return {
      validLength: ownerId.length >= 3 && ownerId.length <= 8,
      validChars: /^[A-Z0-9_]*$/.test(ownerId),
    };
  }, [formData.ownerId]);

  // Reset availability status when ID becomes invalid
  useEffect(() => {
    if (formData.ownerId && (!ownerIdValidation.validLength || !ownerIdValidation.validChars)) {
      setOwnerIdStatus({ isChecking: false, isAvailable: null, suggestions: [] });
    }
  }, [formData.ownerId, ownerIdValidation.validLength, ownerIdValidation.validChars]);

  // Check owner ID availability (debounced)
  const checkOwnerIdAvailability = useCallback(
    async (ownerId: string) => {
      // Don't auto-check, only when button is clicked
      if (!ownerId || ownerId.length < 3 || !/^[A-Z0-9_]+$/.test(ownerId)) {
        setError('Please enter a valid Vastavik ID (3-8 chars, A-Z, 0-9, _)');
        return;
      }

      setOwnerIdStatus((prev) => ({ ...prev, isChecking: true }));

      try {
        const response = await apiService.checkOwnerIdAvailability(
          ownerId,
          formData.firstName,
          formData.lastName,
          formData.email
        );

        if (response.success && response.data) {
          setOwnerIdStatus({
            isChecking: false,
            isAvailable: response.data.available,
            suggestions: response.data.suggestions || [],
          });

          if (!response.data.available) {
            setError(`"${ownerId}" is already taken`);
          } else {
            setError(''); // Clear any previous errors
          }
        } else {
          setOwnerIdStatus({ isChecking: false, isAvailable: null, suggestions: [] });
          setError('Failed to check availability. Please try again.');
        }
      } catch (err) {
        console.error('Owner ID check failed:', err);
        setOwnerIdStatus({ isChecking: false, isAvailable: null, suggestions: [] });
        setError('Network error. Please try again.');
      }
    },
    [formData.firstName, formData.lastName, formData.email]
  );

  const filteredCountries = useMemo(() => {
    if (!countrySearch) return COUNTRY_OPTIONS;

    return COUNTRY_OPTIONS.filter(country =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase())
    );
  }, [countrySearch]);

  useEffect(() => {
    const handleClickOutside = (e: any) => {
      if (!e.target.closest('.country-dropdown-wrapper')) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

// // Debounce owner ID check
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     if (formData.ownerId.length >= 3 && /^[A-Z0-9_]+$/.test(formData.ownerId)) {
  //       checkOwnerIdAvailability(formData.ownerId);
  //     }
  //   }, 500);

  //   return () => clearTimeout(timer);
  // }, [formData.ownerId, checkOwnerIdAvailability]);  // Password strength indicator
  //   // Password strength indicator
  const passwordStrength = useMemo(() => {
    const { password } = formData;
    if (!password) return { level: 0, label: '', color: '' };

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    if (score <= 2) return { level: 1, label: 'Weak', color: 'bg-rose-500' };
    if (score <= 4) return { level: 2, label: 'Fair', color: 'bg-amber-500' };
    if (score <= 5) return { level: 3, label: 'Good', color: 'bg-emerald-400' };
    return { level: 4, label: 'Strong', color: 'bg-emerald-500' };
  }, [formData.password]);

  // Form is valid when all required fields are filled
  const isFormValid = useMemo(() => {
    return (
      formData.email.length > 0 &&
      formData.firstName.length > 0 &&
      formData.lastName.length > 0 &&
      formData.ownerId.length >= 3 &&
      formData.country.length > 0 &&
      isPasswordValid &&
      passwordValidation.passwordsMatch &&
      ownerIdValidation.validChars &&
      ownerIdValidation.validLength &&
      ownerIdStatus.isAvailable === true &&
      acceptTerms
    );
  }, [
    formData,
    isPasswordValid,
    passwordValidation.passwordsMatch,
    ownerIdValidation,
    ownerIdStatus.isAvailable,
    acceptTerms,
  ]);

  // Handle owner ID input (auto uppercase)
  const handleOwnerIdChange = (value: string) => {
    const upperValue = value
      .toUpperCase()
      .replace(/[^A-Z0-9_]/g, '')
      .slice(0, 8);
    setFormData({ ...formData, ownerId: upperValue });
  };

  // Runs for typing AND browser autofill. Keeps formData.country (ISO code)
  // in sync with the visible text so validation passes after autofill.
  const handleCountryInput = (value: string) => {
    setCountrySearch(value);
    setFormData((prev) => ({ ...prev, country: resolveCountryCode(value) }));
  };

  //Normalize displayed text (e.g. "IN" -> "India") when leaving the field
  const handleCountryBlur = () => {
    const code = resolveCountryCode(countrySearch);
    if (code) {
      setCountrySearch(
        countries.getName(code, 'en', { select: 'official' }) || countrySearch
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isFormValid) {
      setError('Please fill in all required fields correctly');
      return;
    }

    setLoading(true);

    try {
      const result = await register(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        formData.ownerId, 
        formData.country
      );

      if (result.success && result.verificationRequired) {
        navigate('/verify-email', {
          state: { email: formData.email },
        });
        return;
      } else if (result.success) {
        setSuccess('Registration successful! You can now login.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const ValidationCheck = ({ isValid, text }: { isValid: boolean; text: string }) => (
    <div className="flex items-center gap-2">
      <div
        className={`
          w-4 h-4 rounded-full flex items-center justify-center text-xs
          transition-all duration-200
          ${isValid ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}
        `}
      >
        {isValid ? (
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <div className="w-1.5 h-1.5 rounded-full bg-current" />
        )}
      </div>
      <span className={`text-[11px] transition-colors ${isValid ? 'text-emerald-600' : 'text-gray-500'}`}>
        {text}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f8f8] py-12 px-4">
      <div 
        className="w-full bg-white flex flex-col relative shadow-[0px_4px_30px_rgba(0,0,0,0.15)]"
        style={{
          maxWidth: '500px', 
          padding: '36px',
          borderRadius: '16px',
          border: '1px solid #222222',
        }}
      >
        {/* Brand Title */}
        <h1 
          className="text-[#131313] text-center leading-none"
          style={{ 
            fontFamily: 'Maxwell, sans-serif', 
            fontSize: '48px', 
          }}
        >
          VASTAVIK
        </h1>

        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3.5 mt-4">
          
          {/* Create Account Header */}
          <h2 
            className="text-black text-center leading-none mb-3"
            style={{ 
              fontFamily: '"Product Sans", sans-serif', 
              fontSize: '32px', 
              fontWeight: 900 // Black/Heavy weight
            }}
          >
            Create Account
          </h2>

          {/* Info Text Block - ✅ UPDATED: Centered, specific color (#484848), spaced paragraphs */}
          <div 
            className="mb-6 text-center flex flex-col gap-4" 
            style={{ 
              color: '#484848',
              fontFamily: '"Product Sans", sans-serif', 
              fontSize: '17px', 
              lineHeight: '1.1',
              letterSpacing: '-0.005em'
            }}
          >
            <div className="font-bold text-[19px]">If you're a,</div>
            <div>
              <span className="font-bold">Creators</span> who publish original digital media online and want audiences to recognise their work as authentic.
            </div>
            <div>
              <span className="font-bold">Public personalities</span> who need to protect their reputation and digital assets from deepfakes, impersonation, and misuse.
            </div>
            <div>
              <span className="font-bold">Enterprises and institutions</span> that want to verify genuine content and avoid being wrongly accused of using AI-generated or manipulated media.
            </div>
          </div>

          {/* Success / Error messages */}
          {success && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-600 text-sm px-4 py-3 flex items-start gap-2" style={{ fontFamily: '"Product Sans", sans-serif' }}>
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{success}</span>
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-600 text-sm px-4 py-3 flex items-start gap-2" style={{ fontFamily: '"Product Sans", sans-serif' }}>
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Name fields - side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col">
              <label className="text-black mb-1" style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '16px', fontWeight: 500 }}>
                First Name<span className="text-[#D32F2F]">*</span>
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                disabled={loading || !!success}
                className="w-full h-[44px] border border-[#222222] rounded-[8px] px-3 text-black bg-white focus:outline-none focus:ring-1 focus:ring-black transition disabled:opacity-50"
                style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '15px' }}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-black mb-1" style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '16px', fontWeight: 500 }}>
                Last Name<span className="text-[#D32F2F]">*</span>
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                disabled={loading || !!success}
                className="w-full h-[44px] border border-[#222222] rounded-[8px] px-3 text-black bg-white focus:outline-none focus:ring-1 focus:ring-black transition disabled:opacity-50"
                style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '15px' }}
              />
            </div>
          </div>

          {/* Email */}
          <div className="flex flex-col">
            <label className="text-black mb-1" style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '16px', fontWeight: 500 }}>
              Email<span className="text-[#D32F2F]">*</span>
            </label>
            <input
              type="email"
              autoComplete="username"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={loading || !!success}
              className="w-full h-[44px] border border-[#222222] rounded-[8px] px-3 text-black bg-white focus:outline-none focus:ring-1 focus:ring-black transition disabled:opacity-50"
              style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '15px' }}
            />
          </div>

          {/* VASTAVIK ID */}
          <div className="flex flex-col">
            <label className="text-black mb-1 flex items-baseline" style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '16px', fontWeight: 500 }}>
              Creator ID<span className="text-[#D32F2F] ml-0.5">*</span>
              <span className="text-gray-500 text-[13px] font-normal ml-2">
                (3-8 chars, your unique public ID)
              </span>
            </label>

            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  autoComplete="off"
                  value={formData.ownerId}
                  onChange={(e) => handleOwnerIdChange(e.target.value)}
                  disabled={loading || !!success}
                  maxLength={8}
                  className={`
                    w-full h-[44px] rounded-[8px] bg-white px-3 pr-10 text-black 
                    border transition disabled:opacity-50 font-mono uppercase tracking-wider
                    ${ownerIdStatus.isAvailable === true
                      ? 'border-emerald-500'
                      : ownerIdStatus.isAvailable === false
                        ? 'border-[#D32F2F]'
                        : ownerIdValidation.validLength && ownerIdValidation.validChars
                          ? 'border-blue-500' 
                          : 'border-[#222222]'
                    }
                    focus:outline-none focus:ring-1 focus:ring-black
                  `}
                  style={{ fontSize: '15px' }}
                />
                
                {/* Status indicator */}
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  {ownerIdStatus.isChecking ? (
                    <svg className="w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : ownerIdStatus.isAvailable === true ? (
                    <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : ownerIdStatus.isAvailable === false ? (
                    <svg className="w-4 h-4 text-[#D32F2F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : null}
                </div>
              </div>

              <button
                type="button"
                onClick={() => checkOwnerIdAvailability(formData.ownerId)}
                disabled={
                  loading ||
                  !!success ||
                  !ownerIdValidation.validLength ||
                  !ownerIdValidation.validChars ||
                  ownerIdStatus.isChecking
                }
                className={`
                  px-5 h-[44px] rounded-[8px] font-medium transition
                  ${ownerIdValidation.validLength && ownerIdValidation.validChars && !ownerIdStatus.isChecking
                    ? 'bg-[#222] text-white hover:bg-black'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }
                `}
                style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '15px' }}
              >
                Check
              </button>
            </div>

            {/* Helper text below input */}
            <p className="text-[13px] text-gray-500 mt-1.5" style={{ fontFamily: '"Product Sans", sans-serif' }}>
              This is your unique public identifier, not your login
            </p>

            {/* Owner ID Hints/Validations */}
            {formData.ownerId && (
              <div className="mt-2 p-2.5 rounded-lg bg-gray-50 border border-gray-200 space-y-1">
                <ValidationCheck isValid={ownerIdValidation.validLength} text="3-8 characters" />
                <ValidationCheck isValid={ownerIdValidation.validChars} text="A-Z, 0-9, _ only" />
                <div className="flex items-center justify-between">
                  <ValidationCheck
                    isValid={ownerIdStatus.isAvailable === true}
                    text={
                      ownerIdStatus.isChecking ? 'Checking availability...' :
                      ownerIdStatus.isAvailable === true ? 'Available!' :
                      ownerIdStatus.isAvailable === false ? 'Already taken' :
                      ownerIdValidation.validLength && ownerIdValidation.validChars ? 'Click "Check" to verify availability' :
                      'Enter valid ID and click Check'
                    }
                  />
                </div>
              </div>
            )}
            
            {/* Suggestions if unavailable */}
            {ownerIdStatus.isAvailable === false && ownerIdStatus.suggestions.length > 0 && (
              <div className="mt-2 p-2.5 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-[11px] text-gray-500 mb-1.5 font-medium">Try these suggestions:</p>
                <div className="flex flex-wrap gap-1.5">
                  {ownerIdStatus.suggestions.slice(0, 4).map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => {
                        handleOwnerIdChange(suggestion);
                        setTimeout(() => checkOwnerIdAvailability(suggestion), 100);
                      }}
                      className="px-2.5 py-1 rounded bg-white hover:bg-gray-100 border border-gray-300 text-black text-[11px] font-mono transition"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Country Selector */}
          <div className="relative country-dropdown-wrapper flex flex-col">
            <label className="text-black mb-1" style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '16px', fontWeight: 500 }}>
              Country<span className="text-[#D32F2F]">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="country"
                autoComplete="country-name"
                value={countrySearch}
                onChange={(e) => {
                  handleCountryInput(e.target.value);
                  setShowCountryDropdown(true);
                }}
                onInput={(e) => handleCountryInput((e.target as HTMLInputElement).value)}
                onBlur={handleCountryBlur}
                onFocus={() => setShowCountryDropdown(true)}
                disabled={loading || !!success}
                className="w-full h-[44px] border border-[#222222] rounded-[8px] px-3 text-black bg-white focus:outline-none focus:ring-1 focus:ring-black transition disabled:opacity-50"
                style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '15px' }}
              />
              {showCountryDropdown && (
                <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg bg-white border border-gray-200 shadow-xl">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, country: country.code }));
                          setCountrySearch(country.name);
                          setShowCountryDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 text-black hover:bg-gray-100 transition"
                        style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '14px' }}
                      >
                        {country.name}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-gray-500 text-sm">No countries found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Hides the native browser password reveal icon (e.g., Microsoft Edge) */}
          <style>{`
            input::-ms-reveal,
            input::-ms-clear {
              display: none;
            }
          `}</style>

          {/* Password */}
          <div className="flex flex-col">
            <label className="text-black mb-1" style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '16px', fontWeight: 500 }}>
              Password<span className="text-[#D32F2F]">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                disabled={loading || !!success}
                className="w-full h-[44px] border border-[#222222] rounded-[8px] px-3 pr-10 text-black bg-white focus:outline-none focus:ring-1 focus:ring-black transition disabled:opacity-50"
                style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '15px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black transition-colors"
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            {/* Password strength meter */}
            {formData.password && (
              <div className="mt-2 space-y-2">
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] text-gray-500">Password strength</span>
                    <span
                      className={`text-[11px] font-medium ${
                        passwordStrength.level <= 1 ? 'text-rose-500' :
                        passwordStrength.level === 2 ? 'text-amber-500' :
                        'text-emerald-600'
                      }`}
                    >
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          level <= passwordStrength.level ? passwordStrength.color : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 grid grid-cols-2 gap-1.5">
                  <ValidationCheck isValid={passwordValidation.minLength} text="8+ chars" />
                  <ValidationCheck isValid={passwordValidation.hasUppercase} text="Uppercase" />
                  <ValidationCheck isValid={passwordValidation.hasLowercase} text="Lowercase" />
                  <ValidationCheck isValid={passwordValidation.hasNumber} text="Number" />
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="flex flex-col">
            <label className="text-black mb-1" style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '16px', fontWeight: 500 }}>
              Confirm Password<span className="text-[#D32F2F]">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                disabled={loading || !!success}
                className={`
                  w-full h-[44px] border rounded-[8px] px-3 pr-10 text-black bg-white 
                  transition disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-black
                  ${formData.confirmPassword
                    ? passwordValidation.passwordsMatch ? 'border-emerald-500' : 'border-[#D32F2F]'
                    : 'border-[#222222]'
                  }
                `}
                style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '15px' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black transition-colors"
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {formData.confirmPassword && (
              <div className={`flex items-center gap-1.5 mt-1.5 text-[11px] ${passwordValidation.passwordsMatch ? 'text-emerald-600' : 'text-[#D32F2F]'}`}>
                {passwordValidation.passwordsMatch ? (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>Passwords match</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>Passwords do not match</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Terms Checkbox*/}
          <div className="flex justify-center items-center gap-2 pt-2 pb-1">
            <input
              type="checkbox"
              id="terms"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              disabled={loading || !!success}
              className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black accent-black cursor-pointer"
            />
            <label htmlFor="terms" className="text-[13px] text-gray-700 cursor-pointer" style={{ fontFamily: '"Product Sans", sans-serif' }}>
              I agree to the{' '}
              <a href="/terms" target="_blank" className="text-black border-b-[1px] border-black pb-[1px] hover:opacity-70 transition-opacity">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" className="text-black border-b-[1px] border-black pb-[1px] hover:opacity-70 transition-opacity">
                Privacy Policy
              </a>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !!success || !isFormValid}
            className={`
              w-full h-[48px] rounded-[8px] transition 
              ${(!loading && !success && isFormValid) 
                ? 'bg-black text-white hover:bg-[#222]' 
                : 'bg-gray-500 text-white opacity-90 cursor-not-allowed'
              }
            `}
            style={{ 
              fontFamily: '"Product Sans", sans-serif', 
              fontSize: '18px',
              fontWeight: 500 
            }}
          >
            {loading ? 'Creating Account...' : success ? 'Account Created!' : 'Create Account'}
          </button>

          {/* Links */}
          <div 
            className="text-center text-[#333333] mt-2"
            style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '13px' }}
          >
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="text-[#047857] font-medium border-b-[1px] border-[#047857] pb-[1px] hover:opacity-70 transition-opacity"
            >
              Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;